import {
  buildOffcutLabelDescriptor,
  buildPlacementCaptionDescriptor,
  buildStripModeBannerDescriptor,
} from "../messages/exportMessages.js";
import { CUT_STRATEGIES } from "./strategies";

/**
 * Export display policy for cut strategies.
 * Strategies affect instructions and visuals only; never the nesting layout.
 */

export function getDisplayOffcuts(offcuts, strategy) {
  return strategy === "preserve_offcuts" ? offcuts.slice(0, 1) : offcuts;
}

export function strategyUsesOffcutDisplay(strategy) {
  return strategy === "preserve_offcuts" || strategy === "strips";
}

export function shouldShowOffcuts(strategy, showOffcutsSetting) {
  return showOffcutsSetting && strategyUsesOffcutDisplay(strategy);
}

export function shouldShowCutSequence(strategy, showCutSequenceSetting) {
  return showCutSequenceSetting && Boolean(CUT_STRATEGIES[strategy]);
}

export function getOffcutLabel(strategy, stripsNoCrosscut) {
  return buildOffcutLabelDescriptor(strategy, stripsNoCrosscut);
}

export function getExportBoardClassName(strategy, stripsNoCrosscut, hasPrimaryOffcut = true) {
  const base = `export-board export-board-${strategy}`;
  const modifiers = [];

  if (stripsNoCrosscut && strategy === "strips") {
    modifiers.push("export-board-no-crosscut");
  }

  if (strategy === "preserve_offcuts" && !hasPrimaryOffcut) {
    modifiers.push("export-board-no-primary-offcut");
  }

  return [base, ...modifiers].join(" ");
}

export function getStripModeBanner(strategy, stripsNoCrosscut) {
  return buildStripModeBannerDescriptor(strategy, stripsNoCrosscut);
}

export function getPlacementCaption(strategy, stripsNoCrosscut, placementName) {
  return buildPlacementCaptionDescriptor(strategy, stripsNoCrosscut, placementName);
}

export function buildStripRipGuides(cutSequence, boardLength, boardWidth) {
  const verticalRips = cutSequence
    .filter((cut) => cut.kind === "strip-rip" && cut.axis === "vertical")
    .map((cut) => Math.round(cut.position))
    .sort((left, right) => left - right);

  const edges = [0, ...verticalRips, boardLength]
    .filter((position, index, values) => index === 0 || position - values[index - 1] > 2)
    .filter((position) => position >= 0 && position <= boardLength);

  const guides = [];
  for (let index = 0; index < edges.length - 1; index += 1) {
    const x = edges[index];
    const length = edges[index + 1] - x;
    if (length <= 2) continue;
    guides.push({
      x,
      y: 0,
      length,
      width: boardWidth,
      label: "STROOK",
    });
  }

  return guides;
}

export function buildFullStripGuides(sheet, sourcePanel) {
  const length = Math.max(Number(sourcePanel.length) || 2440, 40);
  const width = Math.max(Number(sourcePanel.width) || 1220, 40);

  const widthCounts = sheet.placements.reduce((result, placement) => {
    const key = Math.round(placement.width);
    result[key] = (result[key] || 0) + 1;
    return result;
  }, {});

  return sheet.placements.map((placement) => {
    const repeatedWidth = widthCounts[Math.round(placement.width)] > 1;
    const stripWidth = Math.round(placement.width);
    const stripLength = Math.round(placement.length);

    return repeatedWidth
      ? {
          x: 0,
          y: placement.y,
          length,
          width: placement.width,
          label: `VOLLEDIGE STROOK ${stripWidth} mm. NIET AFKORTEN`,
          cutPartLabel: `zaagmaat deel: ${Math.round(placement.length)} × ${stripWidth}`,
        }
      : {
          x: placement.x,
          y: 0,
          length: placement.length,
          width,
          label: `VOLLEDIGE STROOK ${stripLength} mm. NIET AFKORTEN`,
          cutPartLabel: `zaagmaat deel: ${stripLength} × ${Math.round(placement.width)}`,
        };
  });
}
